import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, tap, throwError } from 'rxjs';
import { AssetCollectionClient, AssetCollectionDTO, FilterDTO, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable({
  providedIn: 'root'
})
export class AssetCollectionsService extends ShowInactiveService implements IAdaptorService {

  constructor(
    private assetCollectionClient: AssetCollectionClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService  ) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    this.spinnerService.show();
    return this.assetCollectionClient.searchAssetCollections(skip, take, sortBy, sortDirection, filters)
      .pipe(
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public addRecord(data: AssetCollectionDTO): Observable<any> {
    this.spinnerService.show();
    return this.assetCollectionClient.createAssetCollection(data)
      .pipe(
        tap(() => {
          this.toastService.showSuccess('i18n.domain.assets.asset-collections.messages.create.success-title', 'i18n.domain.assets.asset-collections.messages.create.success-message');
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          else {
            this.toastService.showError('i18n.domain.assets.asset-collections.messages.create.fail-title', 'i18n.domain.assets.asset-collections.messages.create.fail-message');
          }
          return throwError(() => error);
        })
      );
  }

  public updateRecord(data: AssetCollectionDTO): Observable<any> {
    this.spinnerService.show();
    return this.assetCollectionClient.updateAssetCollection(data)
      .pipe(
        tap(() => {
          this.toastService.showSuccess('i18n.domain.assets.asset-collections.messages.update.success-title', 'i18n.domain.assets.asset-collections.messages.update.success-message');
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          else {
            this.toastService.showError('i18n.domain.assets.asset-collections.messages.update.fail-title', 'i18n.domain.assets.asset-collections.messages.update.fail-message');
          }
          return throwError(() => error);
        })
      );
  }

  public deleteRecord(id: number): Observable<any> {
    this.spinnerService.show();
    return this.assetCollectionClient.deleteAssetCollection(id)
      .pipe(
        tap(() => {
          this.toastService.showSuccess('i18n.domain.assets.asset-collections.messages.delete.success-title', 'i18n.domain.assets.asset-collections.messages.delete.success-message');
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          else {
            this.toastService.showError('i18n.domain.assets.asset-collections.messages.delete.fail-title', 'i18n.domain.assets.asset-collections.messages.delete.fail-message');
          }
          return throwError(() => error);
        })
      );
  }

  public getRecord(id: number): Observable<AssetCollectionDTO> {
    this.spinnerService.show();
    return this.assetCollectionClient.getAssetCollection(id)
      .pipe(
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public getAllCollections(): Observable<AssetCollectionDTO[]> {
    return this.getData(0, 1000, [], 'name', SortDirection.Ascending).pipe(
      map((pagedItems: IPagedItems) => {
        return pagedItems.items as AssetCollectionDTO[];
      })
    );
  }
  
}
